import {
	createFileRoute,
	useNavigate,
	useParams,
	useRouteContext,
} from "@tanstack/react-router";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { toast } from "sonner";
import { Heading } from "@/components/heading";
import {
	LessonQuizForm,
	type LessonQuizFormSchemaValues,
} from "@/forms/lesson-quiz-form";
import type { cursosAulasEditQuizQuery } from "./__generated__/cursosAulasEditQuizQuery.graphql";
import type { cursosAulasEditQuizUpdateMutation } from "./__generated__/cursosAulasEditQuizUpdateMutation.graphql";

export const Route = createFileRoute(
	"/_logged/c/$club/(sidebar)/(admins)/area-de-membros/cursos/$slug/aulas/quiz/$lessonSlug",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = useNavigate({ from: Route.fullPath });
	const { slug: courseSlug, lessonSlug } = useParams({ from: Route.id });
	const { club } = useRouteContext({ from: Route.id });

	const data = useLazyLoadQuery<cursosAulasEditQuizQuery>(
		graphql`
			query cursosAulasEditQuizQuery($clubSlug: String!, $courseSlug: String!, $lessonSlug: String!) {
				club(slug: $clubSlug) {
					course(slug: $courseSlug) {
						id
						sections(status: ALL) {
							id
							name
						}
					}
				}
				courseClass(club: $clubSlug, course: $courseSlug, slug: $lessonSlug) {
					__typename
					id
					name
					slug
					description
					section {
						id
					}
					... on CourseQuiz {
						quiz {
							quizType
							questionCount
							questions {
								id
								title
								description
								options {
									id
									text
									isAnswer
								}
							}
						}
					}
				}
			}
		`,
		{ clubSlug: club.slug, courseSlug, lessonSlug },
	);

	const [updateCourseQuiz] = useMutation<cursosAulasEditQuizUpdateMutation>(
		graphql`
			mutation cursosAulasEditQuizUpdateMutation($payload: UpdateCourseQuizInput!) {
				courseQuizUpdate(courseQuizUpdateInput: $payload) {
					id
					name
					slug
					description
					section {
						id
					}
					quiz {
						quizType
						questionCount
						questions {
							id
							title
							description
							options {
								id
								text
								isAnswer
							}
						}
					}
				}
			}
		`,
	);

	const lesson = data.courseClass;

	const sections =
		data.club.course.sections.map((section) => ({
			id: section.id,
			name: section.name,
		})) ?? [];

	// Build default values for quiz form
	const quizDefaultValues: LessonQuizFormSchemaValues =
		lesson.__typename === "CourseQuiz" && lesson.quiz
			? {
					name: lesson.name,
					sectionId: lesson.section.id,
					description: lesson.description || "",
					quizType:
						lesson.quiz.quizType === "REGULAR" ||
						lesson.quiz.quizType === "RANDOM"
							? lesson.quiz.quizType
							: "REGULAR",
					questionCount: lesson.quiz.questionCount ?? null,
					questions: lesson.quiz.questions
						? lesson.quiz.questions.map((q) => ({
								id: q.id,
								title: q.title,
								description: q.description || "",
								options: q.options.map((opt) => ({
									id: opt.id,
									text: opt.text,
									isAnswer: opt.isAnswer,
								})),
							}))
						: [],
				}
			: {
					name: "",
					sectionId: "",
					description: "",
					quizType: "REGULAR",
					questionCount: null,
					questions: [],
				};

	const handleQuizSubmit = (values: LessonQuizFormSchemaValues) => {
		return new Promise<void>((resolve, reject) => {
			updateCourseQuiz({
				variables: {
					payload: {
						id: lesson.id,
						name: values.name,
						section: values.sectionId,
						description: values.description || null,
						quizType: values.quizType,
						questionCount: values.questionCount,
						questions: values.questions.map((q, qIndex) => ({
							id: q.id,
							title: q.title,
							description: q.description || null,
							position: qIndex,
							active: true,
							options: q.options.map((opt, optIndex) => ({
								id: opt.id,
								text: opt.text,
								isAnswer: opt.isAnswer,
								position: optIndex,
							})),
						})),
					},
				},
				onCompleted: (_response, errors) => {
					if (errors?.length) {
						toast.error(errors[0].message);
						reject(new Error(errors[0].message));
						return;
					}
					toast.success("Quiz atualizado com sucesso!");
					navigate({
						to: "/c/$club/area-de-membros/cursos/$slug",
						params: { club: club.slug, slug: courseSlug },
					});
					resolve();
				},
				onError: (error) => {
					console.error("Error updating quiz:", error);
					toast.error("Erro ao atualizar quiz. Tente novamente.");
					reject(error);
				},
			});
		});
	};

	return (
		<div className="size-full flex items-start justify-center py-4 md:py-8">
			<div className="p-6 w-full max-md:px-4 max-w-[700px] flex flex-col gap-6 border rounded-md">
				<Heading>Editar Quiz</Heading>

				<LessonQuizForm
					sections={sections}
					defaultValues={quizDefaultValues}
					onSubmit={handleQuizSubmit}
				/>
			</div>
		</div>
	);
}
